import type { ReactNode } from 'react';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Omitted by editors that hand off instead of saving — the footer drops with it. */
  onSave?: () => void;
  isSaving?: boolean;
  error?: string | null;
  children: ReactNode;
}

/**
 * The chrome every in-place profile editor shares — header, scrolling body and a
 * pinned Save. Mirrors the coach profile's form modals, but the shell lives here
 * once instead of being repeated per modal.
 */
export default function ProfileFormModal({
  isOpen,
  onClose,
  title,
  description,
  onSave,
  isSaving = false,
  error,
  children,
}: Props) {
  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="sm:max-w-[38rem]">
      <div className="flex items-center justify-between border-b border-color-border-input-lightmode px-ds-xl py-ds-lg dark:border-color-border-input-darkmode">
        <h2 className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {title}
        </h2>
        <ButtonText onClick={onClose} aria-label="Close">
          <CloseIcon className="h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
        </ButtonText>
      </div>

      <div className="flex max-h-[65vh] flex-col gap-ds-xl overflow-y-auto px-ds-xl py-ds-xl">
        {description && (
          <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {description}
          </p>
        )}
        {children}
        {error && <p className="typography-product-caption text-color-error">{error}</p>}
      </div>

      {onSave && (
        <div className="flex justify-end border-t border-color-border-input-lightmode px-ds-xl py-ds-lg dark:border-color-border-input-darkmode">
          <Button
            type="button"
            variant="primary"
            size="lg"
            loading={isSaving}
            onClick={onSave}
            className="w-auto min-w-[10rem]"
          >
            Save
          </Button>
        </div>
      )}
    </Modal>
  );
}
